/**
 * Ichimoku Cloud (Ichimoku Kinko Hyo)
 *
 * Multi-component Japanese trend indicator:
 * - Tenkan-sen (Conversion): (9-period HH + LL) / 2
 * - Kijun-sen (Base): (26-period HH + LL) / 2
 * - Senkou Span A: (Tenkan + Kijun) / 2, plotted 26 bars ahead
 * - Senkou Span B: (52-period HH + LL) / 2, plotted 26 bars ahead
 * - Chikou Span: current close plotted 26 bars behind
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const ichimokuIndicator: IndicatorDefinition = {
  id: "ichimoku",
  name: "Ichimoku Cloud",
  shortName: "Ichimoku",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "tenkan", label: "Tenkan Period", type: "number", default: 9, min: 1, max: 200 },
    { key: "kijun", label: "Kijun Period", type: "number", default: 26, min: 1, max: 200 },
    { key: "senkou", label: "Senkou B Period", type: "number", default: 52, min: 1, max: 200 },
    { key: "displacement", label: "Displacement", type: "number", default: 26, min: 1, max: 100 },
  ],
  plots: [
    { key: "tenkan", label: "Tenkan-sen", color: "#2196F3", style: "line", lineWidth: 1 },
    { key: "kijun", label: "Kijun-sen", color: "#EF5350", style: "line", lineWidth: 1 },
    { key: "senkouA", label: "Senkou A", color: "#4CAF50", style: "line", lineWidth: 1, opacity: 0.5 },
    { key: "senkouB", label: "Senkou B", color: "#FF5722", style: "line", lineWidth: 1, opacity: 0.5 },
    { key: "chikou", label: "Chikou Span", color: "#9C27B0", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { tenkan: tenkanPeriod, kijun: kijunPeriod, senkou: senkouPeriod, displacement } = params;

    // Highest-high / lowest-low over period
    const donchianMid = (data: CandleData[], idx: number, period: number): number => {
      if (idx < period - 1) return NaN;
      let high = -Infinity, low = Infinity;
      for (let i = idx - period + 1; i <= idx; i++) {
        if (data[i].high > high) high = data[i].high;
        if (data[i].low < low) low = data[i].low;
      }
      return (high + low) / 2;
    };

    const tenkanData: { time: number; value: number }[] = [];
    const kijunData: { time: number; value: number }[] = [];
    const senkouAData: { time: number; value: number }[] = [];
    const senkouBData: { time: number; value: number }[] = [];
    const chikouData: { time: number; value: number }[] = [];

    for (let i = 0; i < candles.length; i++) {
      const tenkanVal = donchianMid(candles, i, tenkanPeriod);
      const kijunVal = donchianMid(candles, i, kijunPeriod);

      if (!isNaN(tenkanVal)) {
        tenkanData.push({ time: candles[i].time, value: tenkanVal });
      }
      if (!isNaN(kijunVal)) {
        kijunData.push({ time: candles[i].time, value: kijunVal });
      }

      // Senkou Span A = (Tenkan + Kijun) / 2, displaced forward
      if (!isNaN(tenkanVal) && !isNaN(kijunVal)) {
        const futureIdx = i + displacement;
        if (futureIdx < candles.length) {
          senkouAData.push({ time: candles[futureIdx].time, value: (tenkanVal + kijunVal) / 2 });
        }
      }

      // Senkou Span B = (52-period high + low) / 2, displaced forward
      const senkouBVal = donchianMid(candles, i, senkouPeriod);
      if (!isNaN(senkouBVal)) {
        const futureIdx = i + displacement;
        if (futureIdx < candles.length) {
          senkouBData.push({ time: candles[futureIdx].time, value: senkouBVal });
        }
      }

      // Chikou Span = close, displaced backward
      const pastIdx = i - displacement;
      if (pastIdx >= 0) {
        chikouData.push({ time: candles[pastIdx].time, value: candles[i].close });
      }
    }

    return [
      { key: "tenkan", data: tenkanData },
      { key: "kijun", data: kijunData },
      { key: "senkouA", data: senkouAData },
      { key: "senkouB", data: senkouBData },
      { key: "chikou", data: chikouData },
    ];
  },
};
